/**
 * Catalogo scelte RENTRI (codice + etichetta + descrizione).
 * Sostituisce gli input liberi con select guidate, con select guidate descrittive.
 */
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api/client";

export interface RentriChoice {
  value: string;
  label: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Statici (fonte: schemi RENTRI dati_registri/formulari + guide ufficiali)
// ---------------------------------------------------------------------------

export const STATO_FISICO: RentriChoice[] = [
  { value: "S", label: "Solido", description: "Rifiuto in stato solido non polverulento" },
  { value: "SP", label: "Solido polverulento", description: "Rifiuto in polvere o pulverulento" },
  { value: "L", label: "Liquido", description: "Rifiuto in stato liquido" },
  { value: "FP", label: "Fangoso pastoso", description: "Rifiuto fangoso o pastoso" },
  { value: "VS", label: "Vischioso sciropposo", description: "Rifiuto vischioso o sciropposo" },
];

export const PROVENIENZA: RentriChoice[] = [
  { value: "U", label: "Urbano", description: "Rifiuto urbano (D.Lgs. 152/2006)" },
  { value: "S", label: "Speciale", description: "Rifiuto speciale (D.Lgs. 152/2006)" },
];

export const TIPO_MOVIMENTO: RentriChoice[] = [
  { value: "Carico", label: "Carico", description: "Ingresso del rifiuto in unità locale" },
  { value: "Scarico", label: "Scarico", description: "Uscita del rifiuto dall'unità locale" },
];

export const UNITA_MISURA_REGISTRO: RentriChoice[] = [
  { value: "kg", label: "Chilogrammi (kg)", description: "Unità di misura ammessa da RENTRI per i registri" },
  { value: "l", label: "Litri (l)", description: "Unità di misura ammessa da RENTRI per i registri (rifiuti liquidi)" },
];

/** UM estese accettate dal validatore locale (movimento form) — più ampia di RENTRI. */
export const UNITA_MISURA_FORM: RentriChoice[] = [
  { value: "KG", label: "Chilogrammi (kg)" },
  { value: "L", label: "Litri (l)" },
  { value: "T", label: "Tonnellate (t)" },
  { value: "M3", label: "Metri cubi (m³)" },
];

export const RUOLO_CONTATTO: RentriChoice[] = [
  { value: "produttore", label: "Produttore", description: "Soggetto che produce il rifiuto" },
  { value: "trasportatore", label: "Trasportatore", description: "Soggetto che trasporta il rifiuto" },
  { value: "destinatario", label: "Destinatario", description: "Impianto di recupero/smaltimento" },
  { value: "intermediario", label: "Intermediario", description: "Intermediario senza detenzione" },
];

/** Causali operazione DM 59/2023 — fallback statico se il lookup API non è disponibile. */
export const CAUSALI_OPERAZIONE_FALLBACK: RentriChoice[] = [
  { value: "DT", label: "DT", description: "Prodotto o detenuto nell'unità locale" },
  { value: "NP", label: "NP", description: "Nuovo produttore" },
  { value: "T*", label: "T*", description: "Ricevuto da terzi" },
  { value: "RE", label: "RE", description: "Prodotto al di fuori dell'unità locale" },
  { value: "I", label: "I", description: "Scarico interno" },
  { value: "aT", label: "aT", description: "Scarico a terzi" },
  { value: "M", label: "M", description: "Scarico per produzione di materiali" },
  { value: "TR", label: "TR", description: "Intermediario" },
  { value: "T*aT", label: "T*aT", description: "Carico e scarico" },
];

/** Operazioni di recupero (Allegato C parte IV TUA). */
export const OPERAZIONI_R: RentriChoice[] = [
  { value: "R1", label: "R1", description: "Utilizzazione principalmente come combustibile o altro mezzo per produrre energia" },
  { value: "R2", label: "R2", description: "Rigenerazione/recupero di solventi" },
  { value: "R3", label: "R3", description: "Riciclo/recupero delle sostanze organiche non utilizzate come solventi" },
  { value: "R4", label: "R4", description: "Riciclo/recupero dei metalli e dei composti metallici" },
  { value: "R5", label: "R5", description: "Riciclo/recupero di altre sostanze inorganiche" },
  { value: "R6", label: "R6", description: "Rigenerazione degli acidi o delle basi" },
  { value: "R7", label: "R7", description: "Recupero dei prodotti che servono a captare gli inquinanti" },
  { value: "R8", label: "R8", description: "Recupero dei prodotti dai catalizzatori" },
  { value: "R9", label: "R9", description: "Rigenerazione o altri reimpieghi degli oli" },
  { value: "R10", label: "R10", description: "Trattamento in ambiente terrestre a beneficio dell'agricoltura o dell'ecologia" },
  { value: "R11", label: "R11", description: "Utilizzazione di rifiuti ottenuti da una delle operazioni R1–R10" },
  { value: "R12", label: "R12", description: "Scambio di rifiuti per sottoporli a una delle operazioni R1–R11" },
  { value: "R13", label: "R13", description: "Messa in riserva di rifiuti per sottoporli a una delle operazioni R1–R12" },
];

/** Operazioni di smaltimento (Allegato B parte IV TUA). */
export const OPERAZIONI_D: RentriChoice[] = [
  { value: "D1", label: "D1", description: "Deposito sul o nel suolo" },
  { value: "D2", label: "D2", description: "Trattamento in ambiente terrestre" },
  { value: "D3", label: "D3", description: "Iniezioni in profondità" },
  { value: "D4", label: "D4", description: "Lagunaggio" },
  { value: "D5", label: "D5", description: "Messa in discarica specialmente allestita" },
  { value: "D6", label: "D6", description: "Scarico dei rifiuti solidi nell'ambiente idrico" },
  { value: "D7", label: "D7", description: "Immersione, inclusi seppellimento del fondo marino" },
  { value: "D8", label: "D8", description: "Trattamento biologico" },
  { value: "D9", label: "D9", description: "Trattamento fisico-chimico" },
  { value: "D10", label: "D10", description: "Incenerimento a terra" },
  { value: "D11", label: "D11", description: "Incenerimento in mare" },
  { value: "D12", label: "D12", description: "Deposito permanente" },
  { value: "D13", label: "D13", description: "Raggruppamento preliminare prima di una delle operazioni D1–D12" },
  { value: "D14", label: "D14", description: "Ricondizionamento preliminare prima di una delle operazioni D1–D13" },
  { value: "D15", label: "D15", description: "Deposito preliminare prima di una delle operazioni D1–D14" },
];

/** Tipi attività registro (anagrafiche RENTRI v1.0). */
export const ATTIVITA_REGISTRO: RentriChoice[] = [
  { value: "Produzione", label: "Produzione di rifiuti" },
  { value: "Trasporto", label: "Trasporto di rifiuti" },
  { value: "Recupero", label: "Recupero di rifiuti" },
  { value: "Smaltimento", label: "Smaltimento di rifiuti" },
  { value: "CentroRaccolta", label: "Centro di raccolta" },
  { value: "IntermediazioneSenzaDetenzione", label: "Intermediazione (senza detenzione)" },
];

// ---------------------------------------------------------------------------
// Normalizzazione lookup dinamici (codifiche RENTRI)
// ---------------------------------------------------------------------------

/** Estrae value/label/description da un item generico restituito da /codifiche/lookup/<name>. */
export function normalizeCodificaItem(raw: unknown): RentriChoice | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const code = String(r.codice ?? r.code ?? r.id ?? "").trim();
  if (!code) return null;
  const desc = String(r.descrizione ?? r.name ?? r.label ?? "").trim();
  return {
    value: code,
    label: code,
    description: desc || undefined,
  };
}

/** Hook condiviso per i lookup RENTRI (causali, attività, HP, …). */
export function useRentriChoices(name: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["rentri-choices", name],
    queryFn: async (): Promise<RentriChoice[]> => {
      const rows = await endpoints.codifiche.lookup(name);
      const list = Array.isArray(rows) ? rows : [];
      return list
        .map((r) => normalizeCodificaItem(r))
        .filter((x): x is RentriChoice => !!x);
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    enabled: opts.enabled ?? true,
  });
}

/** Formatta un'opzione come "CODICE — Descrizione" (con fallback al solo codice/etichetta). */
export function formatChoiceOption(c: RentriChoice): string {
  if (c.description && c.description !== c.label) {
    return `${c.label} — ${c.description}`;
  }
  return c.label;
}

/** Etichetta leggibile per un codice in un catalogo statico. */
export function choiceLabelByValue(
  value: string | undefined,
  options: RentriChoice[],
): string {
  if (!value) return "—";
  const o = options.find((x) => x.value === value);
  if (!o) return value;
  return o.description ? `${o.label} — ${o.description}` : o.label;
}
